import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import { JoinCompanyData, SignUpData, User } from '../models/user.model';
import { Company } from '../models/company.model';
import { toast } from 'react-toastify';

const apiClient = async (): Promise<AxiosInstance> => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if ('Clerk' in window && window.Clerk.session && window.Clerk.session.status === 'active') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const token = await window.Clerk.session.getToken();
    return axios.create({
      baseURL: process.env.API_URL,
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
};

// Add a request interceptor to include the csrftoken header
/*apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    // Handle request error
    return Promise.reject(error);
  }
);*/

export class NWClient {
  public static signUp(data: SignUpData): Promise<unknown> {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${process.env.API_URL}/api/user/signup/?fehost=${window.location.host}`,
          JSON.stringify(data),
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static acceptInvitation(data: SignUpData | JoinCompanyData): Promise<unknown> {
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${process.env.API_URL}/api/user/invited_link/?fehost=${window.location.host}`,
          JSON.stringify(data),
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static confirmSignUp(uidb64: string, token: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      axios
        .get(`${process.env.API_URL}/activate/${uidb64}/${token}/`)
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static async currentUser(): Promise<User> {
    const axiosClient = await apiClient();
    return axiosClient
      .get('/api/user/current/')
      .then((response: AxiosResponse) => response.data?.[0]);
  }

  public static async getCurrentCompany(): Promise<Company[]> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get('/api/company/')
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static async DisclaimerCheck(): Promise<unknown> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/user/disclaimercheck/`)
        .then((response: AxiosResponse<unknown>) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public static async DisclaimerAccept(): Promise<unknown> {
    const axiosClient = await apiClient();
    return axiosClient
      .patch(`/api/user/disclaimercheck/`, null)
      .then((response: AxiosResponse<unknown>) => response.data)
      .catch((error) => {
        throw error;
      });
  }

  public static async downloadTemplate(id: number, filename: string): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/template/${id}/download_file/`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, filename);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadDocument(id: number, filename: string): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/download-document-upload/${id}`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, filename);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async getDocumentBlob(id: number, filename?: string): Promise<string> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`${process.env.API_URL}/api/download-document-upload/${id}`, {
          responseType: 'blob',
        })
        .then((response: AxiosResponse) => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const fileURL = URL.createObjectURL(blob);
          resolve(fileURL);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadHtmlDocument(
    id: number,
    filename: string,
    filetype?: string
  ): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/document-upload-html-download/${id}?filetype=${filetype}`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, filename);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadLibraryHtmlDocument(
    id: number,
    fileType: string,
    filename?: string,
    ownLibrary?: boolean
  ): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/document-library/${id}/download_file/?fileType=${fileType}`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = filename ? filename : 'downloaded_file';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch && fileNameMatch.length === 1) {
              fileName = fileNameMatch[0];
            }
          }
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, fileName);
          resolve();
          toast.success('File downloaded successfully');
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadLibraryDocument(id: number, filename: string): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/document-library/${id}/download_file/`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = filename ? filename : 'downloaded_file';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch && fileNameMatch.length === 2) {
              fileName = fileNameMatch[1];
            }
          }
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, fileName);
          resolve();
          toast.success('File downloaded successfully');
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadDocumentContract(
    id: number,
    fileName: string,
    fileType: 'pdf' | 'doc'
  ): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(
          `/api/download-document-contract/${id}?doc_type=${fileType}${
            fileName ? `&file_name=${fileName}` : ''
          }`,
          {
            responseType: 'arraybuffer',
          }
        )
        .then((response: AxiosResponse) => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, fileName);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async downloadTemplateInstance(id: number, filename: string): Promise<void> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/template-instance/${id}/download_instance/`, {
          responseType: 'arraybuffer',
        })
        .then((response: AxiosResponse) => {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          saveAs(blob, filename);
          resolve();
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async list<T>(entity: string, params?: unknown, data?: unknown): Promise<T[]> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/${entity}/`, {
          params: params,
          data: data,
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async get<T>(entity: string, id: number | string, params?: unknown): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/${entity}/${id + ''}/`, {
          params: params,
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async getByUser<T>(entity: string, user: number): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .get(`/api/${entity}?user=${user}`)
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async post<T>(
    entity: string,
    data: Omit<T, 'id'>,
    contentTypeObject?: boolean,
    urlParam?: string
  ): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .post(`/api/${entity}/${urlParam ? urlParam : ''}`, data, {
          headers: {
            'Content-Type': contentTypeObject ? 'multipart/form-data' : 'application/json',
          },
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async put<T>(
    entity: string,
    id: number,
    data: Omit<T, 'id'>,
    closingSlash?: boolean,
    contentTypeObject?: boolean
  ): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .put(`/api/${entity}/${id}${closingSlash ? '/' : ''}`, data, {
          headers: {
            'Content-Type': contentTypeObject ? 'multipart/form-data' : 'application/json',
          },
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async patch<T>(entity: string, id: number | null, data: Partial<T>): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      const url = id ? `/api/${entity}/${id}` : `/api/${entity}`;
      axiosClient
        .patch(url, data)
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }

  public static async delete<T>(
    entity: string,
    id: number | string,
    params?: unknown,
    data?: unknown
  ): Promise<T> {
    const axiosClient = await apiClient();
    return new Promise((resolve, reject) => {
      axiosClient
        .delete(`/api/${entity}/${id + ''}/`, {
          params: params,
          data: data,
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  }
}
